import { useMemo, useEffect } from "react";
import { useWorldStore } from "@/stores/worldStore";
import { useReviewStore } from "@/stores/reviewStore";
import { useAiChatStore } from "@/stores/aiChatStore";
import { useChatTabStore } from "@/stores/chatTabStore";
import { useLocalTerminalStore } from "@/stores/localTerminalStore";
import { extractProjectIdFromScope } from "@/lib/utils/projects";
import {
  type AgentSource,
  type UnifiedAgentRow,
  type UnifiedAgentGroup,
  mapRunToUnifiedRow,
  mapChatToUnifiedRow,
  mapTerminalToUnifiedRow,
  groupByProject,
} from "@/lib/data/unified-agents";

interface UnifiedAgentsFilter {
  projectId?: number;
  source?: AgentSource;
}

export function useUnifiedAgents(
  filter?: UnifiedAgentsFilter,
): UnifiedAgentGroup[] {
  const projects = useWorldStore((s) => s.projects);
  const runs = useReviewStore((s) => s.runs);
  const sessions = useAiChatStore((s) => s.sessions);
  const chatTabs = useChatTabStore((s) => s.tabs);
  const terminalScopes = useLocalTerminalStore((s) => s.scopes);

  useEffect(() => {
    if (projects.length === 0) return;
    const validIds = new Set(projects.map((p) => p.id));
    useChatTabStore.getState().purgeOrphanedTabs(validIds);
  }, [projects]);

  return useMemo(() => {
    const projectMap = new Map(projects.map((p) => [p.id, p]));
    const rows: UnifiedAgentRow[] = [];

    // Workflow runs
    if (!filter?.source || filter.source === "workflow") {
      for (const run of runs) {
        if (filter?.projectId != null && run.projectId !== filter.projectId) continue;
        const project = projectMap.get(run.projectId);
        rows.push(mapRunToUnifiedRow(run, project?.name ?? run.projectName));
      }
    }

    // Chat tabs + sessions
    if (!filter?.source || filter.source === "chat") {
      for (const [pidStr, tabs] of Object.entries(chatTabs)) {
        const pid = Number(pidStr);
        if (filter?.projectId != null && pid !== filter.projectId) continue;
        const project = projectMap.get(pid);
        for (const tab of tabs) {
          const session = tab.sessionId ? sessions.get(tab.sessionId) : undefined;
          rows.push(mapChatToUnifiedRow(tab, session, project?.name ?? `Project ${pid}`));
        }
      }
    }

    // Terminal tabs
    if (!filter?.source || filter.source === "terminal") {
      for (const [scopeKey, scope] of Object.entries(terminalScopes)) {
        const pid = extractProjectIdFromScope(scopeKey, projects);
        if (filter?.projectId != null && pid !== filter.projectId) continue;
        const project = pid != null ? projectMap.get(pid) : undefined;
        for (const tab of scope.tabs) {
          rows.push(
            mapTerminalToUnifiedRow(
              tab,
              scopeKey,
              pid,
              project?.name ?? "Ungrouped",
              project?.localPath,
            ),
          );
        }
      }
    }

    return groupByProject(rows);
  }, [projects, runs, sessions, chatTabs, terminalScopes, filter?.projectId, filter?.source]);
}
